<template>
  <!-- 메모·연락 기록 타임라인 (docs/architecture/admin-member-management-design.md 3절) -->
  <section class="notes-card">
    <div class="notes-head">
      <h2 class="notes-title">
        메모·연락 기록
        <span v-if="!loading && !loadError" class="notes-count">{{
          notes.length
        }}</span>
      </h2>
      <div class="notes-head-actions">
        <button
          type="button"
          class="notes-add-btn"
          :class="{ active: addMode === 'contact' }"
          :disabled="!userId"
          @click="openAdd('contact')"
        >
          + 연락
        </button>
        <button
          type="button"
          class="notes-add-btn"
          :class="{ active: addMode === 'memo' }"
          :disabled="!userId"
          @click="openAdd('memo')"
        >
          + 메모
        </button>
      </div>
    </div>

    <!-- 새 기록 -->
    <form v-if="addMode" class="note-form" @submit.prevent="submitAdd">
      <div class="note-form-label">
        {{ addMode === "contact" ? "연락 기록" : "메모" }}
      </div>
      <div
        v-if="addMode === 'contact'"
        class="result-group"
        role="radiogroup"
        aria-label="연락 결과"
      >
        <label
          v-for="r in contactResults"
          :key="r.value"
          class="result-option"
          :class="[
            resultTone(r.value),
            { checked: addForm.contact_result === r.value },
          ]"
        >
          <input
            v-model="addForm.contact_result"
            type="radio"
            name="member-note-add-result"
            :value="r.value"
          />
          {{ r.label }}
        </label>
        <span v-if="!addForm.contact_result" class="result-hint"
          >결과를 골라 주세요</span
        >
      </div>
      <textarea
        ref="addTextarea"
        v-model="addForm.content"
        class="note-textarea"
        rows="3"
        :maxlength="maxLength"
        :placeholder="
          addMode === 'contact' ? '연락 내용 (선택)' : '메모 내용 (필수)'
        "
      />
      <div class="note-form-foot">
        <span class="char-count"
          >{{ addForm.content.length }}/{{ maxLength }}</span
        >
        <div class="note-form-actions">
          <button type="button" class="note-btn ghost" @click="requestCloseAdd">
            취소
          </button>
          <button
            type="submit"
            class="note-btn primary"
            :disabled="!canSubmitAdd || saving"
          >
            {{ saving ? "저장 중..." : "저장" }}
          </button>
        </div>
      </div>
    </form>

    <div v-if="loading" class="notes-state">기록을 불러오는 중...</div>
    <div v-else-if="loadError" class="notes-state error">
      <p>기록을 불러오지 못했습니다.</p>
      <button type="button" class="note-btn ghost" @click="load">
        다시 시도
      </button>
    </div>
    <div v-else-if="notes.length === 0" class="notes-state">
      아직 남긴 기록이 없습니다.
    </div>
    <ul v-else class="note-list">
      <li v-for="note in notes" :key="note.id" class="note-item">
        <div class="note-meta">
          <span class="note-date">{{ noteDateTime(note.created_at) }}</span>
          <span class="note-author">{{
            note.author_name || "작성자 없음"
          }}</span>
          <span class="note-badge" :class="badgeTone(note)">{{
            badgeLabel(note)
          }}</span>
          <span v-if="note.edited" class="note-edited">(수정됨)</span>
          <div
            v-if="note.editable && editingId !== note.id"
            class="note-actions"
          >
            <!-- 다른 기록을 저장·삭제하는 동안에는 모두 꺼진다(한 번에 한 요청) -->
            <button
              type="button"
              class="note-action-btn"
              :disabled="busyId !== null"
              @click="startEdit(note)"
            >
              수정
            </button>
            <button
              type="button"
              class="note-action-btn danger"
              :disabled="busyId !== null"
              @click="removeNote(note)"
            >
              삭제
            </button>
          </div>
        </div>

        <p v-if="editingId !== note.id && note.content" class="note-content">
          {{ note.content }}
        </p>

        <!-- 본인 기록 수정 (종류는 못 바꾼다) -->
        <form
          v-if="editingId === note.id"
          class="note-form inline"
          @submit.prevent="submitEdit(note)"
        >
          <div
            v-if="note.kind === 'contact'"
            class="result-group"
            role="radiogroup"
            aria-label="연락 결과"
          >
            <label
              v-for="r in contactResults"
              :key="r.value"
              class="result-option"
              :class="[
                resultTone(r.value),
                { checked: editForm.contact_result === r.value },
              ]"
            >
              <input
                v-model="editForm.contact_result"
                type="radio"
                name="member-note-edit-result"
                :value="r.value"
              />
              {{ r.label }}
            </label>
          </div>
          <textarea
            v-model="editForm.content"
            class="note-textarea"
            rows="3"
            :maxlength="maxLength"
            :placeholder="
              note.kind === 'contact' ? '연락 내용 (선택)' : '메모 내용 (필수)'
            "
          />
          <div class="note-form-foot">
            <span class="char-count"
              >{{ editForm.content.length }}/{{ maxLength }}</span
            >
            <div class="note-form-actions">
              <button
                type="button"
                class="note-btn ghost"
                :disabled="busyId === note.id"
                @click="requestCancelEdit(note)"
              >
                취소
              </button>
              <button
                type="submit"
                class="note-btn primary"
                :disabled="!canSubmitEdit(note) || busyId !== null"
              >
                {{ busyId === note.id ? "저장 중..." : "저장" }}
              </button>
            </div>
          </div>
        </form>
      </li>
    </ul>
  </section>
</template>

<script>
import {
  CONTACT_RESULTS,
  contactResultLabel,
  contactResultTone,
  noteDateTime,
  confirmDiscardDraft,
  fetchNotes,
  createNote,
  updateNote,
  deleteNote,
} from "@/lib/memberNotes";

// 서버 검증과 같은 길이 (앞뒤 공백을 뺀 내용 1000자)
const MAX_CONTENT_LENGTH = 1000;
const RESULT_VALUES = CONTACT_RESULTS.map((r) => r.value);

function emptyAddForm() {
  return { contact_result: "", content: "" };
}

export default {
  name: "MemberNotes",
  props: {
    userId: { type: String, default: "" },
  },
  data() {
    return {
      notes: [],
      loading: false,
      loadError: false,
      loadSeq: 0, // 회원이 바뀌는 사이 늦게 온 이전 회원 응답을 버리기 위한 번호
      // 새 기록 폼: null | "contact" | "memo"
      addMode: null,
      addForm: emptyAddForm(),
      saving: false,
      // 수정 중인 기록
      editingId: null,
      editForm: { contact_result: "", content: "" },
      busyId: null, // 수정·삭제 요청 중인 기록 id
      contactResults: CONTACT_RESULTS,
      maxLength: MAX_CONTENT_LENGTH,
    };
  },
  computed: {
    // 연락은 결과가 필수(내용은 선택), 메모는 내용이 필수
    canSubmitAdd() {
      const content = this.addForm.content.trim();
      if (content.length > MAX_CONTENT_LENGTH) return false;
      if (this.addMode === "contact") {
        return RESULT_VALUES.includes(this.addForm.contact_result);
      }
      if (this.addMode === "memo") return content.length > 0;
      return false;
    },
  },
  watch: {
    // 다른 회원 상세로 옮기면 그 회원 기록을 다시 읽고 열린 폼은 닫는다
    userId: {
      immediate: true,
      handler() {
        this.closeAdd();
        this.cancelEdit();
        this.load();
      },
    },
  },
  methods: {
    async load() {
      const seq = ++this.loadSeq;
      if (!this.userId) {
        this.notes = [];
        this.loading = false;
        this.loadError = false;
        return;
      }
      this.loading = true;
      this.loadError = false;
      try {
        const notes = await fetchNotes(this.userId);
        if (seq !== this.loadSeq) return;
        this.notes = notes;
      } catch (e) {
        if (seq !== this.loadSeq) return;
        // 오류 문구는 apiJson의 전역 오류 팝업이 보인다. 여기서는 목록 자리에 실패만 표시
        console.error("메모·연락 기록 로딩 실패:", e);
        this.notes = [];
        this.loadError = true;
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },
    noteDateTime,
    badgeLabel(note) {
      if (note.kind === "contact") {
        return contactResultLabel(note.contact_result) || "연락";
      }
      return "메모";
    },
    // 통화·문자 초록, 부재 노랑, 메모는 무채색
    badgeTone(note) {
      if (note.kind === "contact") {
        return contactResultTone(note.contact_result) || "neutral";
      }
      return "neutral";
    },
    resultTone(value) {
      return contactResultTone(value) || "neutral";
    },
    // ---------------- 추가 ----------------
    openAdd(kind) {
      // 다른 종류로 바꿔도 쓰던 내용은 남긴다
      if (this.addMode !== kind) {
        this.addMode = kind;
        if (kind === "memo") this.addForm.contact_result = "";
      }
      this.$nextTick(() => {
        if (kind === "memo" && this.$refs.addTextarea) {
          this.$refs.addTextarea.focus();
        }
      });
    },
    closeAdd() {
      this.addMode = null;
      this.addForm = emptyAddForm();
    },
    // [취소]: 쓰던 글이 있으면 연락 기록 모달처럼 한 번 묻는다
    requestCloseAdd() {
      if (this.addHasDraft() && !confirmDiscardDraft()) return;
      this.closeAdd();
    },
    // 연락은 고른 결과만 있어도 쓰던 기록이다(부재는 보통 내용 없이 남긴다, 4차 리뷰: 결과만 고른 폼은 묻지 않고 사라졌다)
    addHasDraft() {
      if (!this.addMode) return false;
      if (this.addForm.content.trim()) return true;
      return this.addMode === "contact" && !!this.addForm.contact_result;
    },
    // 열린 수정 폼에서 내용이나 연락 결과를 바꿨는지
    editHasDraft() {
      if (this.editingId === null) return false;
      const note = this.notes.find((n) => n.id === this.editingId);
      if (!note) return false;
      return (
        this.editForm.content.trim() !== (note.content || "").trim() ||
        (note.kind === "contact" &&
          this.editForm.contact_result !== (note.contact_result || ""))
      );
    },
    // 쓰던 글(새 기록 내용, 바꾼 수정 폼)이 있는지. 회원 상세(ProfileView)가 화면을 떠나기 전에 묻는 데 쓴다
    hasDraft() {
      return this.addHasDraft() || this.editHasDraft();
    },
    async submitAdd() {
      if (!this.canSubmitAdd || this.saving || !this.userId) return;
      const kind = this.addMode;
      const payload = { kind, content: this.addForm.content.trim() };
      if (kind === "contact") {
        payload.contact_result = this.addForm.contact_result;
      }
      const userId = this.userId;
      this.saving = true;
      try {
        const created = await createNote(userId, payload);
        if (userId !== this.userId) return;
        this.closeAdd();
        if (created && created.id) {
          // 응답 = 만든 기록 한 건. 최신 순이므로 맨 위에 둔다
          this.notes = [
            created,
            ...this.notes.filter((n) => n.id !== created.id),
          ];
        } else {
          await this.load();
        }
      } catch (e) {
        // 실패 문구는 전역 오류 팝업. 쓰던 내용은 그대로 둔다
        console.error("메모·연락 기록 저장 실패:", e);
      } finally {
        this.saving = false;
      }
    },
    // ---------------- 수정 ----------------
    startEdit(note) {
      if (!note.editable || this.busyId || this.editingId === note.id) return;
      // 다른 기록의 수정 폼에 바꾼 글이 있으면 [취소]처럼 묻는다. 예전에는 다른 기록의 [수정]을 누르면 쓰던 글이 말없이 사라졌다(4차 리뷰)
      if (this.editHasDraft() && !confirmDiscardDraft()) return;
      this.editingId = note.id;
      this.editForm = {
        contact_result: note.contact_result || "",
        content: note.content || "",
      };
    },
    cancelEdit() {
      this.editingId = null;
      this.editForm = { contact_result: "", content: "" };
    },
    requestCancelEdit(note) {
      if (this.editingId === note.id && this.editHasDraft()) {
        if (!confirmDiscardDraft()) return;
      }
      this.cancelEdit();
    },
    canSubmitEdit(note) {
      const content = this.editForm.content.trim();
      if (content.length > MAX_CONTENT_LENGTH) return false;
      if (note.kind === "contact") {
        return RESULT_VALUES.includes(this.editForm.contact_result);
      }
      return content.length > 0;
    },
    async submitEdit(note) {
      if (!this.canSubmitEdit(note) || this.busyId) return;
      // 서버는 보낸 칸만 바꾼다. 바뀐 칸만 보내고, 바뀐 것이 없으면 닫기만 한다
      const fields = {};
      const content = this.editForm.content.trim();
      if (content !== (note.content || "")) fields.content = content;
      if (
        note.kind === "contact" &&
        this.editForm.contact_result !== note.contact_result
      ) {
        fields.contact_result = this.editForm.contact_result;
      }
      if (Object.keys(fields).length === 0) {
        this.cancelEdit();
        return;
      }
      this.busyId = note.id;
      try {
        const updated = await updateNote(note.id, fields);
        this.replaceNote(
          note.id,
          updated && updated.id ? updated : { ...note, ...fields, edited: true }
        );
        // 그 사이 다른 기록의 수정 폼이 열렸으면 그 폼(쓰던 글)은 두고 닫지 않는다
        if (this.editingId === note.id) this.cancelEdit();
      } catch (e) {
        console.error("메모·연락 기록 수정 실패:", e);
        this.reloadIfGone(e, note.id);
      } finally {
        this.busyId = null;
      }
    },
    replaceNote(id, next) {
      this.notes = this.notes.map((n) => (n.id === id ? next : n));
    },
    // ---------------- 삭제 ----------------
    async removeNote(note) {
      if (!note.editable || this.busyId) return;
      if (!confirm("이 기록을 삭제할까요?\n삭제하면 되돌릴 수 없습니다.")) {
        return;
      }
      this.busyId = note.id;
      try {
        await deleteNote(note.id);
        this.notes = this.notes.filter((n) => n.id !== note.id);
        if (this.editingId === note.id) this.cancelEdit();
      } catch (e) {
        console.error("메모·연락 기록 삭제 실패:", e);
        this.reloadIfGone(e, note.id);
      } finally {
        this.busyId = null;
      }
    },
    // 이미 지워졌거나(404) 내 기록이 아니면(403) 화면이 낡은 것이므로 다시 읽는다. 그 기록의 수정 폼만 닫는다
    reloadIfGone(e, noteId) {
      const status = e?.response?.status;
      if (status === 403 || status === 404) {
        if (this.editingId === noteId) this.cancelEdit();
        this.load();
      }
    },
  },
};
</script>

<style scoped>
.notes-card {
  background: #151718;
  border-radius: 10px;
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.notes-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.notes-title {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 140%;
  letter-spacing: -0.02em;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
}

.notes-count {
  font-size: 13px;
  font-weight: 500;
  color: #8e8e8e;
  background: #0a0a0a;
  border-radius: 10px;
  padding: 1px 8px;
}

.notes-head-actions {
  display: flex;
  gap: 8px;
}

.notes-add-btn {
  padding: 6px 12px;
  background: transparent;
  border: 1px solid #333;
  border-radius: 5px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.notes-add-btn:hover:not(:disabled),
.notes-add-btn.active {
  border-color: #78ff47;
  color: #78ff47;
}

.notes-add-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* 입력 폼 */
.note-form {
  background: #0a0a0a;
  border: 1px solid #2a2d2f;
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.note-form.inline {
  margin-top: 8px;
  background: #111314;
}

.note-form-label {
  font-size: 13px;
  font-weight: 600;
  color: #8b9096;
}

.result-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.result-option {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border: 1px solid #333;
  border-radius: 16px;
  font-size: 13px;
  color: #c4c4c4;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
}

/* 라디오 원은 숨기고 알약 모양 버튼으로 고른다 (키보드 선택은 그대로) */
.result-option input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  margin: 0;
}

/* 초점 표시는 키보드일 때만, 고른 상태(초록·노랑)와 헷갈리지 않게 흰색 (ContactLogModal과 같다) */
.result-option:has(input:focus-visible) {
  outline: 2px solid rgba(255, 255, 255, 0.85);
  outline-offset: 2px;
}

.result-option:hover {
  border-color: #555;
  color: #fff;
}

.result-option.checked.safe {
  background: rgba(34, 197, 94, 0.15);
  border-color: #22c55e;
  color: #22c55e;
}

.result-option.checked.warning {
  background: rgba(251, 191, 36, 0.15);
  border-color: #fbbf24;
  color: #fbbf24;
}

.result-hint {
  font-size: 12px;
  color: #8e8e8e;
}

.note-textarea {
  width: 100%;
  box-sizing: border-box;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.5;
  color: #fff;
  background: #1c1f21;
  border: 1px solid #3a3d3f;
  border-radius: 8px;
  padding: 10px 12px;
  outline: none;
  resize: vertical;
  /* 쓰는 중에도 읽을 때(.note-content)처럼 한국어 낱말 중간에서 줄을 바꾸지 않는다(3차 리뷰) */
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.note-textarea:focus {
  border-color: #78ff47;
}

/* 좁은 단(1101px 안팎 화면의 왼쪽 단)에서는 버튼을 다음 줄로 넘기고 글자는 쪼개지 않는다 */
.note-form-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.char-count {
  font-size: 12px;
  color: #6b7075;
}

.note-form-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.note-btn {
  white-space: nowrap;
  padding: 6px 14px;
  border-radius: 5px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid #333;
  transition: opacity 0.2s;
}

.note-btn.ghost {
  background: transparent;
  color: #fff;
}

.note-btn.primary {
  background: #78ff47;
  border-color: #78ff47;
  color: #000;
  font-weight: 600;
}

.note-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* 상태 */
.notes-state {
  text-align: center;
  padding: 20px;
  color: #8e8e8e;
  font-size: 14px;
}

.notes-state.error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.notes-state.error p {
  margin: 0;
  color: #ef4444;
}

/* 타임라인 */
.note-list {
  list-style: none;
  margin: 0;
  padding: 0 4px 0 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 600px;
  overflow-y: auto;
}

.note-list::-webkit-scrollbar {
  width: 6px;
}

.note-list::-webkit-scrollbar-track {
  background: #2a2a2a;
  border-radius: 3px;
}

.note-list::-webkit-scrollbar-thumb {
  background: #555;
  border-radius: 3px;
}

.note-item {
  background: #0a0a0a;
  border-radius: 10px;
  padding: 12px 14px;
}

.note-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  font-size: 13px;
}

.note-date {
  color: #8e8e8e;
  font-variant-numeric: tabular-nums;
}

.note-author {
  color: #fff;
  font-weight: 600;
}

.note-badge {
  font-size: 12px;
  padding: 1px 8px;
  border-radius: 4px;
}

.note-badge.safe {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.note-badge.warning {
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
}

.note-badge.neutral {
  background: rgba(142, 142, 142, 0.15);
  color: #c4c4c4;
}

.note-edited {
  font-size: 12px;
  color: #6b7075;
}

.note-actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
}

.note-action-btn {
  white-space: nowrap;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid #333;
  background: transparent;
  color: #8e8e8e;
  transition: all 0.2s;
}

.note-action-btn:hover:not(:disabled) {
  border-color: #78ff47;
  color: #78ff47;
}

.note-action-btn.danger:hover:not(:disabled) {
  border-color: #ef4444;
  color: #ef4444;
}

.note-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.note-content {
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: #e5e5e5;
  white-space: pre-wrap;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

@media (max-width: 720px) {
  .notes-card {
    padding: 16px;
  }
}
</style>
